<template>
  <div v-if="show_facility_modal"
    class="fixed inset-0 bg-gray-900 bg-opacity-75 flex justify-center items-center z-50 backdrop-blur-sm">
    <div class="bg-white rounded-md shadow-xl w-full max-w-6xl">
      <!-- Modal Header -->
      <div class="flex justify-between items-center p-6 border-b">
        <h2 class="text-xl font-semibold">{{ isEditing ? 'Edit Facility' : 'Add New Facility' }}</h2>
        <button @click="closeModal" class="text-gray-400 hover:text-gray-600">
          <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 18" />
          </svg>
        </button>
      </div>
      <!-- Stepper Navigation -->
      <div class="flex">
        <button @click="currentStep = 'facility-details'"
          class="flex-1 px-6 py-4 font-medium text-sm relative transition-all" :class="{
            'text-orange-400': currentStep === 'facility-details',
            'text-gray-500 bg-gray-100': currentStep !== 'facility-details'
          }">
          <span class="flex items-center justify-center">
            <span class="w-6 h-6 rounded-full border-2 mr-2 flex items-center justify-center text-xs font-bold"
              :class="{
                'bg-white text-orange-400 border-orange-400': currentStep === 'facility-details',
                'bg-transparent border-gray-400': currentStep !== 'facility-details'
              }">
              1
            </span>
            Facility Details
          </span>
        </button>
        <button @click="goToSpecialistsStep"
          class="flex-1 px-6 py-4 font-medium text-sm relative transition-all" :class="{
            'text-orange-400': currentStep === 'facility-specialists',
            'text-gray-500 bg-gray-100': currentStep !== 'facility-specialists'
          }">
          <span class="flex items-center justify-center">
            <span class="w-6 h-6 rounded-full border-2 mr-2 flex items-center justify-center text-xs font-bold"
              :class="{
                'bg-white text-orange-400 border-orange-400': currentStep === 'facility-specialists',
                'bg-transparent border-gray-400': currentStep !== 'facility-specialists'
              }">
              2
            </span>
            Facility Specialists
          </span>
        </button>
      </div>
      <!-- Step Content -->
      <div class="p-6">
        <!-- Facility Details Step -->
        <div v-if="currentStep === 'facility-details'">
          <h3 class="text-lg font-medium mb-6">Facility Details</h3>
          <div v-if="isFetching" class="flex justify-center items-center">
            <svg class="animate-spin h-5 w-5 text-orange-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor"
                d="M4 12A8 12 0 018 4a8 8 0 0 1 8 8v0v-8C4 0 0 5.373 0 12h4zm2 5.291A7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3"></path>
            </svg>
            <span class="ml-2 text-gray-600">Loading services...</span>
          </div>
          <div v-else class="space-y-4">
            <div>
              <label for="facility_name" class="block text-sm font-medium text-gray-700">
                <span class="text-red-500">*</span> Facility Name
              </label>
              <input id="facility_name" v-model="formData.name" type="text" required
                class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200" />
            </div>
            <div>
              <label for="facility_phone" class="block text-sm font-medium text-gray-700">
                <span class="text-red-500">*</span> Facility Phone
              </label>
              <input id="facility_phone" v-model="formData.phone" type="tel" required
                class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200" />
            </div>
            <div>
              <label for="facility_email" class="block text-sm font-medium text-gray-700">
                <span class="text-red-500">*</span> Facility Email
              </label>
              <input id="facility_email" v-model="formData.email" type="email" required
                class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200" />
            </div>
            <div>
              <label for="facility_address" class="block text-sm font-medium text-gray-700">
                <span class="text-red-500">*</span> Facility Address
              </label>
              <input id="facility_address" v-model="formData.address" type="text" required
                class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200" />
            </div>
          </div>
          <!-- Facility Services Section -->
          <div v-if="!isFetching" class="mt-8">
            <h4 class="text-base font-semibold text-gray-900 mb-4">Facility Services</h4>
            <div v-if="serviceOptions.length === 0" class="text-gray-600">
              No services available. Please add services first.
            </div>
            <div v-else class="space-y-3 max-h-60 overflow-auto">
              <div v-for="(service, index) in formData.services" :key="index"
                class="grid grid-cols-12 gap-3 items-end">
                <div class="col-span-4">
                  <label v-if="index === 0" class="block text-sm font-medium text-gray-600 mb-1">Service Name</label>
                  <select v-model="service.name" @change="prefillServiceType(index)"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200">
                    <option value="">Select Service</option>
                    <option v-for="serviceOption in serviceOptions" :key="serviceOption.id" :value="serviceOption.name">
                      {{ serviceOption.name }}
                    </option>
                  </select>
                </div>
                <div class="col-span-4">
                  <label v-if="index === 0" class="block text-sm font-medium text-gray-600 mb-1">Service Type</label>
                  <input v-model="service.type" type="text" readonly
                    class="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 focus:outline-none" />
                </div>
                <div class="col-span-3">
                  <label v-if="index === 0" class="block text-sm font-medium text-gray-600 mb-1">Cost (KES)</label>
                  <input v-model="service.cost" type="number" placeholder="0"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200" />
                </div>
                <div class="col-span-1">
                  <button @click="removeService(index)" v-if="formData.services.length > 1"
                    class="p-2 text-red-500 hover:bg-red-50 rounded">
                    <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24"
                      stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            <div class="flex justify-end">
              <button @click="addService" :disabled="serviceOptions.length === 0"
                class="mt-4 text-orange-400 hover:text-orange-500 font-semibold text-sm flex items-center"
                :class="{ 'opacity-50 cursor-not-allowed': serviceOptions.length === 0 }">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Add Service
              </button>
            </div>
          </div>
          <!-- Success Message -->
          <div v-if="successMessage" class="mt-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-md">
            {{ successMessage }}
          </div>
          <!-- Error Message -->
          <div v-if="errorMessage" class="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
            {{ errorMessage }}
          </div>
          <!-- Navigation Buttons -->
          <div class="flex justify-end mt-8 pt-6 border-t">
            <button @click="closeModal"
              class="px-6 py-2 text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors">
              Cancel
            </button>
            <button @click="submitFacilityAndServices"
              :disabled="isSubmitting || isFetching || serviceOptions.length === 0"
              class="px-6 py-2 ml-3 bg-orange-400 text-white rounded-md hover:bg-orange-500 transition-colors flex items-center"
              :class="{ 'opacity-50 cursor-not-allowed': isSubmitting || isFetching || serviceOptions.length === 0 }">
              <svg v-if="isSubmitting" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg"
                fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {{ isSubmitting ? 'Submitting...' : isEditing ? 'Update' : 'Next' }}
            </button>
          </div>
        </div>
        <!-- Facility Specialists Step -->
        <div v-if="currentStep === 'facility-specialists'">
          <h3 class="text-lg font-medium mb-6">Facility Specialists</h3>
          <div v-if="isFetchingServices" class="flex justify-center items-center">
            <svg class="animate-spin h-5 w-5 text-orange-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span class="ml-2 text-gray-600">Loading facility services...</span>
          </div>
          <div v-else-if="facilityServices.length === 0" class="text-gray-600">
            No services available for this facility.
          </div>
          <div v-else class="space-y-4 max-h-96 overflow-auto">
            <div v-for="(specialist, index) in formData.specialists" :key="index"
              class="grid grid-cols-12 gap-3 items-end">
              <div class="col-span-3">
                <label v-if="index === 0" class="block text-sm font-medium text-gray-600 mb-1">Specialist Name</label>
                <input v-model="specialist.name" type="text" placeholder="Specialist Name"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200" />
              </div>
              <div class="col-span-3">
                <label v-if="index === 0" class="block text-sm font-medium text-gray-600 mb-1">Specialist Email</label>
                <input v-model="specialist.email" type="email" placeholder="Email"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200" />
              </div>
              <div class="col-span-2">
                <label v-if="index === 0" class="block text-sm font-medium text-gray-600 mb-1">ID Number</label>
                <input v-model="specialist.id_number" type="text" placeholder="ID Number"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200" />
              </div>
              <div class="col-span-3">
                <label v-if="index === 0" class="block text-sm font-medium text-gray-600 mb-1">Services Offered</label>
                <div class="relative" @click.stop>
                  <div @click="toggleDropdown(index)" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200 cursor-pointer h-10 overflow-y-auto flex flex-wrap items-center gap-1">
                    <span v-if="specialist.services.length === 0" class="text-gray-500">Select services</span>
                    <span v-for="serviceId in specialist.services" :key="serviceId" class="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm flex items-center gap-1 flex-shrink-0">
                      {{ getServiceName(serviceId) }}
                      <button @click.stop="removeServiceFromSpecialist(index, serviceId)" class="text-blue-600 hover:text-blue-800">&times;</button>
                    </span>
                  </div>
                  <div v-if="dropdownOpen[index]" class="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                    <div v-for="service in facilityServices" :key="service.id" class="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center">
                      <input type="checkbox" :checked="specialist.services.includes(service.id)" @change="toggleServiceForSpecialist(index, service.id)" class="mr-2" />
                      {{ service.name }}
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-span-1">
                <button @click="removeSpecialist(index)" v-if="formData.specialists.length > 1"
                  class="p-2 text-red-500 hover:bg-red-50 rounded">
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
            <div class="flex justify-end">
              <button @click="addSpecialist"
                class="mt-4 text-orange-400 hover:text-orange-500 font-semibold text-sm flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Add Specialist
              </button>
            </div>
            <!-- Success Message -->
            <div v-if="successMessage" class="mt-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-md">
              {{ successMessage }}
            </div>
            <!-- Error Message -->
            <div v-if="errorMessage" class="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
              {{ errorMessage }}
            </div>
            <!-- Navigation Buttons -->
            <div class="flex justify-between mt-8 pt-6 border-t">
              <button @click="currentStep = 'facility-details'"
                class="px-6 py-2 text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors">
                Back
              </button>
              <div class="flex space-x-3">
                <button @click="submitSpecialists"
                  :disabled="isSubmitting || isFetchingServices || facilityServices.length === 0"
                  class="px-6 py-2 bg-orange-400 text-white rounded-md hover:bg-orange-500 transition-colors flex items-center"
                  :class="{ 'opacity-50 cursor-not-allowed': isSubmitting || isFetchingServices || facilityServices.length === 0 }">
                  <svg v-if="isSubmitting" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg"
                    fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {{ isSubmitting ? 'Submitting...' : isEditing ? 'Update Specialists' : 'Submit Specialists' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted } from 'vue';
import { useToast } from 'vue-toastification';
import { FacilitiesAPI, SpecialistsAPI, SpecializedServicesAPI } from '@/services';

const props = defineProps({
  show_facility_modal: {
    type: Boolean,
    required: true,
  },
  facility: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['close_facility_modal', 'facility-submitted', 'specialists-submitted']);

const toast = useToast();

const currentStep = ref('facility-details');
const facilityId = ref(null);
const facilityServices = ref([]);
const serviceOptions = ref([]);
const isSubmitting = ref(false);
const isFetching = ref(false);
const isFetchingServices = ref(false);
const errorMessage = ref(null);
const successMessage = ref(null);
const isEditing = ref(false);
const dropdownOpen = ref({});

const formData = ref({
  name: '',
  phone: '',
  email: '',
  address: '',
  services: [{ name: '', type: '', cost: '', id: '' }],
  specialists: [{ name: '', email: '', id_number: '', services: [] }],
});

const toggleDropdown = (index) => {
  dropdownOpen.value[index] = !dropdownOpen.value[index];
};

const getServiceName = (serviceId) => {
  const service = facilityServices.value.find(s => s.id === serviceId);
  return service ? service.name : '';
};

const removeServiceFromSpecialist = (specialistIndex, serviceId) => {
  const specialist = formData.value.specialists[specialistIndex];
  specialist.services = specialist.services.filter(id => id !== serviceId);
};

const toggleServiceForSpecialist = (specialistIndex, serviceId) => {
  const specialist = formData.value.specialists[specialistIndex];
  if (specialist.services.includes(serviceId)) {
    specialist.services = specialist.services.filter(id => id !== serviceId);
  } else {
    specialist.services.push(serviceId);
  }
};

const closeDropdowns = () => {
  dropdownOpen.value = {};
};

const fetchServices = async () => {
  try {
    isFetching.value = true;
    errorMessage.value = null;
    const response = await SpecializedServicesAPI.getAll();
    console.log('Services Response:', JSON.stringify(response, null, 2));
    if (response.data && response.data.data) {
      serviceOptions.value = response.data.data.map(service => ({
        id: service.id,
        name: service.name,
        type: service.service_type,
      }));
      console.log('Fetched Services:', serviceOptions.value);
    } else {
      serviceOptions.value = [];
      errorMessage.value = 'No services found.';
    }
  } catch (error) {
    console.error('Error fetching services:', error, error.response);
    errorMessage.value = 'Failed to load services. Please try again.';
    serviceOptions.value = [];
  } finally {
    isFetching.value = false;
  }
};

const fetchFacilityServices = async (id) => {
  try {
    isFetchingServices.value = true;
    errorMessage.value = null;
    const response = await FacilitiesAPI.getOne(id);
    console.log('Facility Services Response:', JSON.stringify(response, null, 2));
    
    if (response.data && response.data.success && response.data.data.specialized_services_with_cost) {
      facilityServices.value = response.data.data.specialized_services_with_cost.map(service => ({
        id: service.id,
        pivot_id: service.pivot_id,
        name: service.name,
        type: service.service_type,
        cost: parseFloat(service.cost),
        _destroy: false
      }));
      
      if (isEditing.value) {
        formData.value.services = [...facilityServices.value];
      }
      
      if (facilityServices.value.length === 0) {
        errorMessage.value = 'No services associated with this facility.';
      }
    } else {
      errorMessage.value = 'Failed to fetch facility services.';
      facilityServices.value = [];
    }
  } catch (error) {
    console.error('Error fetching facility services:', error, error.response);
    errorMessage.value = error.response?.data?.message || 'Failed to fetch facility services. Please try again.';
    facilityServices.value = [];
  } finally {
    isFetchingServices.value = false;
  }
};

const prefillFormData = async () => {
  if (props.facility && props.facility.id) {
    isEditing.value = true;
    facilityId.value = props.facility.id;
    formData.value = {
      name: props.facility.name || '',
      phone: props.facility.phone || '',
      email: props.facility.email || '',
      address: props.facility.address || '',
      services: props.facility.specialized_services_with_cost?.length
        ? props.facility.specialized_services_with_cost.map(service => ({
            id: service.id,
            name: service.name,
            type: service.service_type,
            cost: parseFloat(service.cost) || '',
          }))
        : [{ name: '', type: '', cost: '', id: '' }],
      specialists: props.facility.specialists?.length
        ? props.facility.specialists.map(specialist => ({
            id: specialist.id,
            name: specialist.name,
            id_number: specialist.id_number,
            email: specialist.email,
            services: specialist.services?.map(s => s.id) || [],
          }))
        : [{ name: '', id_number: '', email: '', services: [] }],
    };
    formData.value.services.forEach((service, index) => {
      if (service.name) {
        prefillServiceType(index);
      }
    });
    if (facilityId.value) {
      await fetchFacilityServices(facilityId.value);
      formData.value.specialists.forEach(specialist => {
        if (specialist.service && !facilityServices.value.some(service => service.id === specialist.service)) {
          specialist.service = '';
        }
      });
    }
  } else {
    isEditing.value = false;
    resetFormData();
  }
};

const addService = () => {
  formData.value.services.push({ name: '', type: '', cost: '', id: '' });
};

const removeService = (index) => {
  if (formData.value.services.length > 1) {
    if (isEditing.value && formData.value.services[index].id) {
      formData.value.services[index]._destroy = true;
    } else {
      formData.value.services.splice(index, 1);
    }
  }
};

const prefillServiceType = (index) => {
  const selectedService = serviceOptions.value.find(
    option => option.name === formData.value.services[index].name
  );
  if (selectedService) {
    formData.value.services[index].type = selectedService.type;
    formData.value.services[index].id = selectedService.id;
  } else {
    formData.value.services[index].type = '';
    formData.value.services[index].id = '';
  }
};

const addSpecialist = () => {
  formData.value.specialists.push({ name: '', id_number: '', email: '', services: [] });
};

const removeSpecialist = async (index) => {
  if (formData.value.specialists.length > 1) {
    const specialist = formData.value.specialists[index];
    if (isEditing.value && specialist.id) {
      try {
        const response = await SpecialistsAPI.delete(specialist.id);
        if (response.data?.success) {
          formData.value.specialists.splice(index, 1);
          toast.success(`Specialist ${specialist.name} deleted successfully`);
        } else {
          toast.error(`Failed to delete specialist ${specialist.name}: ${response.data?.message || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Error deleting specialist:', error);
        toast.error(`Failed to delete specialist ${specialist.name}: ${error.response?.data?.message || error.message}`);
      }
    } else {
      formData.value.specialists.splice(index, 1);
    }
  }
};

const goToSpecialistsStep = () => {
  if (!facilityId.value && !isEditing.value) {
    errorMessage.value = 'Please complete the facility details step first.';
    return;
  }
  currentStep.value = 'facility-specialists';
};

const validateFacilityForm = () => {
  if (!formData.value.name || !formData.value.phone || !formData.value.email || !formData.value.address) {
    errorMessage.value = 'Please fill in all required facility fields.';
    return false;
  }
  const hasInvalidServices = formData.value.services.some(service =>
    !service._destroy && (!service.name || isNaN(service.cost) || !service.id)
  );
  if (hasInvalidServices) {
    errorMessage.value = 'Please complete all service fields and ensure costs are valid numbers.';
    return false;
  }
  return true;
};

const submitFacilityAndServices = async () => {
  if (!validateFacilityForm()) {
    return;
  }
  
  if (formData.value.services.some(service => !service._destroy && (!service.name || !service.cost || !service.id))) {
    errorMessage.value = 'Please complete all service fields.';
    return;
  }

  isSubmitting.value = true;
  errorMessage.value = null;
  successMessage.value = null;

  try {
    let servicesAttributes = [];
    
    if (isEditing.value) {
      // For editing: include pivot_id for existing services
      servicesAttributes = formData.value.services.map(service => {
        const serviceData = {
          specialized_service_id: service.id,
          cost: parseFloat(service.cost) || 0,
        };

        // Include the join table record ID for existing services
        if (service.pivot_id) {
          serviceData.id = service.pivot_id;
        }

        // Handle service deletion
        if (service._destroy) {
          serviceData._destroy = true;
        }

        return serviceData;
      });
    } else {
      // For creating: no pivot_id needed
      servicesAttributes = formData.value.services
        .filter(service => !service._destroy)
        .map(service => ({
          specialized_service_id: service.id,
          cost: parseFloat(service.cost) || 0,
        }));
    }

    const payload = {
      specialized_facility: {
        name: formData.value.name,
        phone: formData.value.phone,
        email: formData.value.email,
        address: formData.value.address,
        specialized_facility_services_attributes: servicesAttributes,
      },
    };

    console.log('Submitting facility payload:', JSON.stringify(payload, null, 2));

    let response;
    if (isEditing.value && facilityId.value) {
      response = await FacilitiesAPI.update(facilityId.value, payload);
      console.log('Facility Update Response:', JSON.stringify(response, null, 2));
      successMessage.value = 'Facility updated successfully! Proceeding to specialists.';
    } else {
      response = await FacilitiesAPI.create(payload);
      console.log('Facility Creation Response:', JSON.stringify(response, null, 2));
      successMessage.value = 'Facility created successfully! Proceeding to specialists.';
      facilityId.value = response.data.data.id;
    }

    if (response.data && response.data.success) {
      emit('facility-submitted', response.data);
      await fetchFacilityServices(facilityId.value);
      currentStep.value = 'facility-specialists';
      setTimeout(() => {
        successMessage.value = null;
      }, 2000);
    } else {
      throw new Error(response.data?.data?.message || (isEditing.value ? 'Failed to update facility' : 'Failed to create facility'));
    }
  } catch (err) {
    console.error('Error processing facility:', err, err.response?.data);
    errorMessage.value = err.response?.data?.message || (isEditing.value ? 'Failed to update facility. Please try again.' : 'Failed to create facility. Please try again.');
  } finally {
    isSubmitting.value = false;
  }
};

const submitSpecialists = async () => {
  if (!facilityId.value) {
    errorMessage.value = 'Facility ID not found. Please complete the first step.';
    return;
  }

  // Validate that all non-destroyed specialists have required fields
  const activeSpecialists = formData.value.specialists.filter(spec => !spec._destroy);
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (activeSpecialists.some(specialist => !specialist.name || !specialist.email || !emailRegex.test(specialist.email) || !specialist.id_number || specialist.services.length === 0)) {
    errorMessage.value = 'Please complete all specialist fields with valid email format and select at least one service.';
    return;
  }

  isSubmitting.value = true;
  errorMessage.value = null;
  successMessage.value = null;

  try {
    const results = [];
    
    // Process each specialist
    for (const specialist of formData.value.specialists) {

      // Prepare payload for create/update
      const payload = {
        specialized_facility_id: facilityId.value,
        specialized_service_ids: specialist.services,
        name: specialist.name,
        id_number: specialist.id_number,
        email: specialist.email,
      };

      console.log('Processing specialist payload:', JSON.stringify(payload, null, 2));

      let response;
      if (specialist.id) {
        // Update existing specialist
        response = await SpecialistsAPI.update(specialist.id, payload);
        console.log('Specialist Update Response:', JSON.stringify(response, null, 2));
        results.push({ action: 'updated', specialist: specialist.name });
      } else {
        // Create new specialist
        response = await SpecialistsAPI.create(payload);
        console.log('Specialist Create Response:', JSON.stringify(response, null, 2));
        
        // Update the local specialist object with the returned ID
        if (response.data?.success && response.data?.data?.id) {
          specialist.id = response.data.data.id;
        }
        results.push({ action: 'created', specialist: specialist.name });
      }

      if (!response.data?.success) {
        throw new Error(response.data?.message || `Failed to process specialist: ${specialist.name}`);
      }
    }

    // Generate success message based on actions performed
    const actions = results.reduce((acc, result) => {
      acc[result.action] = (acc[result.action] || 0) + 1;
      return acc;
    }, {});

    const actionMessages = [];
    if (actions.created) actionMessages.push(`${actions.created} specialist(s) created`);
    if (actions.updated) actionMessages.push(`${actions.updated} specialist(s) updated`);
    if (actions.deleted) actionMessages.push(`${actions.deleted} specialist(s) deleted`);

    successMessage.value = actionMessages.length > 0 
      ? `Success: ${actionMessages.join(', ')}`
      : 'Specialists processed successfully!';

    emit('specialists-submitted', { success: true, results });
    
    setTimeout(() => {
      successMessage.value = null;
      if (closeModal) closeModal();
    }, 2000);

  } catch (err) {
    console.error('Error processing specialists:', err, err.response?.data);
    errorMessage.value = err.response?.data?.message || err.message || 'Failed to process specialists. Please try again.';
  } finally {
    isSubmitting.value = false;
  }
};

const closeModal = () => {
  emit('close_facility_modal');
  resetFormData();
};

const resetFormData = () => {
  formData.value = {
    name: '',
    phone: '',
    email: '',
    address: '',
    services: [{ name: '', type: '', cost: '', id: '' }],
    specialists: [{ name: '', email: '', id_number: '', services: [] }],
  };
  currentStep.value = 'facility-details';
  facilityId.value = null;
  facilityServices.value = [];
  errorMessage.value = null;
  successMessage.value = null;
  isEditing.value = false;
  isFetching.value = false;
  isFetchingServices.value = false;
  dropdownOpen.value = {};
};

onMounted(async () => {
  console.log('NewBookingFacilityModal mounted');
  resetFormData();
  await fetchServices();
  await prefillFormData();

  document.addEventListener('click', closeDropdowns);
});

onUnmounted(() => {
  document.removeEventListener('click', closeDropdowns);
});

watch(() => props.show_facility_modal, async (newVal) => {
  if (newVal) {
    console.log('Modal opened, resetting state');
    resetFormData();
    await fetchServices();
    await prefillFormData();
  }
});

watch(() => props.facility, async () => {
  console.log('Facility prop changed, prefilling form');
  await prefillFormData();
});
</script>